import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Tag, Share2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BlogDetailPage = () => {
  const { postId } = useParams();
  const { t, language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const url = process.env.REACT_APP_BACKEND_URL || '';
        const res = await axios.get(`${url}/api/blog/${postId}`);
        setPost(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [postId]);

  const getField = (p, field) => p[`${field}_${language}`] || p[`${field}_de`];

  const renderMarkdown = (text) => {
    if (!text) return '';
    return text
      .split('\n')
      .map((line, i) => {
        if (line.startsWith('## ')) {
          return <h2 key={i} className="text-2xl font-bold text-black dark:text-white mt-8 mb-4">{line.replace('## ', '')}</h2>;
        }
        if (line.startsWith('- ')) {
          return <li key={i} className="text-dark-grey dark:text-gray-400 ml-4 mb-1">{line.replace('- ', '')}</li>;
        }
        if (line.trim() === '') {
          return <br key={i} />;
        }
        return <p key={i} className="text-dark-grey dark:text-gray-400 leading-relaxed mb-3">{line}</p>;
      });
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({ title: getField(post, 'title'), url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Header />
      <main className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-2 border-mid-purple border-t-transparent rounded-full" />
            </div>
          ) : !post ? (
            <div className="text-center py-20">
              <p className="text-dark-grey dark:text-gray-400 mb-4">{t('blog.notFound')}</p>
              <Link to="/blog" className="text-mid-purple hover:underline">{t('blog.backToList')}</Link>
            </div>
          ) : (
            <article>
              <Link to="/blog" className="inline-flex items-center gap-2 text-dark-grey dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors mb-8">
                <ArrowLeft size={16} />
                {t('blog.backToList')}
              </Link>

              {/* Header Image */}
              {post.image && (
                <div className="relative rounded-2xl overflow-hidden mb-8 h-64 md:h-80">
                  <img src={post.image} alt={getField(post, 'title')} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                </div>
              )}

              {/* Meta */}
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="text-sm text-mid-grey dark:text-gray-500 flex items-center gap-1">
                  <Calendar size={14} />
                  {new Date(post.created_at).toLocaleDateString(language === 'de' ? 'de-DE' : language === 'ar' ? 'ar-SA' : 'en-US', { day: '2-digit', month: 'long', year: 'numeric' })}
                </span>
                {post.tags && post.tags.map((tag, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-mid-purple/10 dark:bg-mid-purple/20 text-mid-purple rounded-full text-xs">
                    <Tag size={10} />{tag}
                  </span>
                ))}
                <button onClick={handleShare} className="ml-auto flex items-center gap-1 text-sm text-dark-grey dark:text-gray-400 hover:text-mid-purple transition-colors" data-testid="share-btn">
                  <Share2 size={14} />
                  {t('blog.share')}
                </button>
              </div>

              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-black dark:text-white mb-8">
                {getField(post, 'title')}
              </h1>

              {/* Content */}
              <div className="prose-custom">
                {renderMarkdown(getField(post, 'content'))}
              </div>

              {/* CTA */}
              <div className="mt-16 p-8 bg-gradient-to-r from-mid-purple/10 to-mid-pink/10 dark:from-mid-purple/20 dark:to-mid-pink/20 rounded-2xl text-center">
                <h3 className="font-display text-2xl font-bold text-black dark:text-white mb-3">{t('blog.ctaTitle')}</h3>
                <p className="text-dark-grey dark:text-gray-400 mb-6">{t('blog.ctaText')}</p>
                <Link
                  to="/#contact"
                  className="inline-flex items-center gap-2 bg-black dark:bg-white text-white dark:text-black px-8 py-3 rounded-full font-mono text-sm uppercase tracking-wider btn-lift"
                >
                  {t('blog.ctaButton')}
                </Link>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogDetailPage;
